import dayjs from 'dayjs';
import { useCallback, useId } from 'react';
import { useSearchParams } from 'react-router-dom';
import { REGISTER_ID_SEARCH_PARAM_KEY } from '@/modules/common/constants/search-params.ts';
import { useRegisterTypeStrings } from '@/modules/common/hooks/useRegisterTypeStrings.tsx';
import { useIssuerDividendEventInfiniteListQuery } from '@/modules/issuer-dashboard/queries/dividends.tsx';
import { SelectPaginate, SelectPaginateOption } from 'ui';

interface DividendEventsSelectPaginateProps {
  disabled?: boolean;
  disableWhileLoading?: boolean;
  entityId?: number;
  error?: string;
  onChange: (option: SelectPaginateOption<number> | null) => void;
  value?: SelectPaginateOption<number> | null;
}

export const DividendEventsSelectPaginate = ({
  disabled = false,
  disableWhileLoading = false,
  entityId,
  error,
  onChange,
  value,
}: DividendEventsSelectPaginateProps) => {
  const getRegisterTypeString = useRegisterTypeStrings();
  const [params] = useSearchParams();
  const registerId = params.get(REGISTER_ID_SEARCH_PARAM_KEY);

  const { fetchNextPage, isFetching, isFetchingNextPage, isLoading } =
    useIssuerDividendEventInfiniteListQuery(
      { page: 1, page_size: 15, ...(entityId ? { entity_id: entityId } : {}) },
      { cacheKey: useId() },
    );
  const isDependentLoading = isLoading || (isFetching && !isFetchingNextPage);
  const isLoadingOptions = isDependentLoading || isFetchingNextPage;

  const handleFetchNextDividends = async (
    page: number,
  ): Promise<SelectPaginateOption<number>[]> => {
    const dividendsData = await fetchNextPage({ pageParam: page });

    const dividends = dividendsData?.data?.pages?.at(-1)?.data || [];
    return dividends.map(({ id, register_class, date, name }) => ({
      label: (
        <div className="flex justify-between pr-2 items-center">
          <span className="flex gap-1 items-center">
            <span>{name}</span>
            <span>{register_class.class_name}</span>
          </span>
          <span className="text-xs text-gray-500">{dayjs(date).format('YYYY-MM-DD, HH:mm')}</span>
        </div>
      ),
      value: id,
    }));
  };

  const handleChange = useCallback(
    (option: SelectPaginateOption<number> | null) => {
      onChange(option);
    },
    [onChange],
  );

  return (
    <SelectPaginate
      value={value}
      cacheUniqs={[registerId ?? null, entityId ?? null]}
      label={`${getRegisterTypeString('dividend')} event`}
      error={error}
      onAsync={handleFetchNextDividends}
      onChange={handleChange}
      isClearable={true}
      isLoading={isLoadingOptions}
      disabled={disabled || (disableWhileLoading && isDependentLoading)}
    />
  );
};
